import React, { useEffect, useState } from 'react';
import { FiTrash } from 'react-icons/fi';
import { API_BASE_URL } from '../config';
import { authFetch } from '../auth';
import { useMaterial } from '../MaterialContext';

/**
 * Componente para listar compras importadas.
 * - Exibe uma tabela com nome, status e botão para remover.
 * - Os dados são carregados do backend (endpoint GET /compras/all).
 * - Ao clicar no ícone de lixeira, a compra é removida via DELETE /compras/:id,
 *   que também apaga o arquivo utilizado na importação.
 */
const ListarCompras = () => {
  const { materialType } = useMaterial();
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasId = compras.length > 0 && compras[0]._id !== undefined;

  const getCompraName = (compra) => compra?.description ?? compra?.nome ?? compra?.descricao ?? '';

  const renderStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="status-badge badge-green">Ativa</span>;
    }
    if (status === 'pending') {
      return <span className="status-badge badge-yellow">Pendente</span>;
    }
    return status ?? '—';
  };

  useEffect(() => {
    fetchCompras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompras = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${API_BASE_URL}/compras/all?material_type=${materialType}`);

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      const data = await response.json();
      setCompras(data);
    } catch (err) {
      console.error('Erro ao carregar compras:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove a compra do banco de dados e o arquivo importado, e atualiza a lista local.
   * A API esperada é DELETE /compras/:id.
   */
  const handleDelete = async (compraId) => {
    const confirm = window.confirm('Tem certeza que deseja remover esta compra?');
    if (!confirm) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/compras/${compraId}?material_type=${materialType}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }
      setCompras((prev) => prev.filter((compra) => compra._id !== compraId));
    } catch (err) {
      console.error('Erro ao remover compra:', err);
      alert('Erro ao remover compra. Tente novamente.');
    }
  };

  if (loading) {
    return <div className="page-placeholder">Carregando compras...</div>;
  }

  if (error) {
    return <div className="page-placeholder error">Erro: {error}</div>;
  }

  return (
    <div className="listar-compras">
      <h1 className="page-title">Compras</h1>

      {compras.length === 0 ? (
        <p>Nenhuma compra encontrada.</p>
      ) : (
        <div className="table-container">
          <table className="stock-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Status</th>
                {hasId && <th>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {compras.map((compra, index) => (
                <tr key={hasId ? compra._id : index}>
                  <td>{getCompraName(compra)}</td>
                  <td>{renderStatusBadge(compra.status)}</td>
                  {hasId && (
                    <td>
                      <FiTrash
                        size={18}
                        className="delete-icon"
                        title="Remover compra"
                        onClick={() => handleDelete(compra._id)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .listar-compras {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }
        .delete-icon {
          cursor: pointer;
          color: #e53e3e;
        }
        .delete-icon:hover {
          color: #c53030;
        }
        .error {
          color: #e53e3e;
        }
      `}</style>
    </div>
  );
};

export default ListarCompras;
